import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Analytics.css';

/* ── Data ──────────────────────────────────────────── */
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const REVENUE  = [180000,210000,195000,240000,258000,0,0,0,0,0,0,0];
const LEAD_SOURCES = [
  {label:'Meta Ads',value:38,color:'#F5A623'},{label:'Referral',value:28,color:'#3B82F6'},
  {label:'Website',value:20,color:'#22C55E'},{label:'Google',value:10,color:'#8B5CF6'},
  {label:'Cold',value:4,color:'#EF4444'},
];
const PIPELINE = [
  {label:'New',value:12,color:'#22C55E'},{label:'Contacted',value:9,color:'#3B82F6'},
  {label:'Warm',value:10,color:'#60A5FA'},{label:'Hot',value:7,color:'#F5A623'},
  {label:'Closed',value:6,color:'#22C55E'},{label:'Lost',value:2,color:'#EF4444'},
];
const TOP_CLIENTS = [
  {name:'Rao Infra',value:120000,city:'Kochi'},{name:'Singh Logistics',value:88000,city:'Delhi'},
  {name:'Nair Exports',value:72000,city:'Kozhikode'},{name:'Techwave',value:45000,city:'Chennai'},
  {name:'Sneha Corp',value:55000,city:'Mumbai'},
];
const SCATTER_DATA = [
  {x:12,y:180000,label:'Jan'},{x:15,y:210000,label:'Feb'},{x:11,y:195000,label:'Mar'},
  {x:18,y:240000,label:'Apr'},{x:22,y:258000,label:'May'},
];
const FUNNEL = [
  {label:'Visitors',value:1200,color:'#3B82F6'},{label:'Leads',value:480,color:'#8B5CF6'},
  {label:'Qualified',value:210,color:'#F5A623'},{label:'Proposals',value:85,color:'#F97316'},
  {label:'Closed',value:28,color:'#22C55E'},
];
const HEATMAP_DATA = Array.from({length:7},(_,d)=>Array.from({length:12},(_,h)=>Math.floor(Math.random()*10)));
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const CLIENT_LOCATIONS = [
  {city:'Kochi',clients:3,lat:9.9312,lng:76.2673},{city:'Chennai',clients:2,lat:13.0827,lng:80.2707},
  {city:'Delhi',clients:2,lat:28.6139,lng:77.2090},{city:'Mumbai',clients:2,lat:19.0760,lng:72.8777},
  {city:'Kozhikode',clients:1,lat:11.2588,lng:75.7804},{city:'Bangalore',clients:1,lat:12.9716,lng:77.5946},
  {city:'Hyderabad',clients:1,lat:17.3850,lng:78.4867},{city:'Dubai',clients:1,lat:25.2048,lng:55.2708},
  {city:'Singapore',clients:1,lat:1.3521,lng:103.8198},
];

const fmtINR = v => v>=100000?'₹'+(v/100000).toFixed(1)+'L':'₹'+(v/1000).toFixed(0)+'K';

/* ── Widget Registry ───────────────────────────────── */
const ALL_WIDGETS = [
  {id:'stats',    label:'KPI Cards',        icon:'📊', desc:'Revenue, leads, tasks, team'},
  {id:'revenue',  label:'Revenue Trend',    icon:'📈', desc:'Line chart — monthly'},
  {id:'area',     label:'Area Chart',       icon:'📉', desc:'Revenue cumulative area'},
  {id:'vbar',     label:'Vertical Bars',    icon:'📊', desc:'Monthly revenue bars'},
  {id:'sources',  label:'Lead Sources',     icon:'🥧', desc:'Pie by source'},
  {id:'pipeline', label:'Pipeline Bars',    icon:'▬',  desc:'Horizontal bars by status'},
  {id:'scatter',  label:'Scatter Plot',     icon:'✦',  desc:'Leads vs Revenue'},
  {id:'funnel',   label:'Funnel Chart',     icon:'▽',  desc:'Lead conversion stages'},
  {id:'heatmap',  label:'Activity Heatmap', icon:'▦',  desc:'7-day activity grid'},
  {id:'clients',  label:'Top Clients',      icon:'🏆', desc:'By deal value'},
];
const DEFAULT_ACTIVE = ['stats','revenue','sources','pipeline'];
const MAX_WIDGETS = 8;

/* ── Three.js Globe (Shopify-style dotted) ─────────────── */
function Globe3D({ size = 'full' }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const W = mount.clientWidth || 400;
    const H = mount.clientHeight || 400;

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W/H, 0.1, 1000);
    camera.position.z = size === 'mini' ? 2.7 : 2.15;

    const renderer = new THREE.WebGLRenderer({ antialias:true, alpha:true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    /* Dark inner sphere */
    const sphereMat = new THREE.MeshBasicMaterial({ color: size==='mini' ? 0x06050d : 0x08070f, transparent:true, opacity:0.97 });
    group.add(new THREE.Mesh(new THREE.SphereGeometry(0.985, 32, 32), sphereMat));

    /* Dot grid — fibonacci sphere distribution */
    const N = size === 'mini' ? 600 : 2200;
    const pos = new Float32Array(N * 3);
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = goldenAngle * i;
      pos[i*3]   = r * Math.cos(theta);
      pos[i*3+1] = y;
      pos[i*3+2] = r * Math.sin(theta);
    }
    const dotGeo = new THREE.BufferGeometry();
    dotGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));

    /* Circular sprite texture */
    const sc = document.createElement('canvas'); sc.width = 16; sc.height = 16;
    const sctx = sc.getContext('2d');
    sctx.beginPath(); sctx.arc(8, 8, 7, 0, Math.PI*2);
    sctx.fillStyle = '#fff'; sctx.fill();
    const spriteTex = new THREE.CanvasTexture(sc);

    const dotMat = new THREE.PointsMaterial({
      size: size === 'mini' ? 0.038 : 0.025,
      map: spriteTex, transparent:true, opacity: size==='mini'?0.25:0.18,
      depthWrite:false, sizeAttenuation:true,
    });
    group.add(new THREE.Points(dotGeo, dotMat));

    /* Subtle equator ring */
    group.add(new THREE.Mesh(
      new THREE.TorusGeometry(1.005, 0.002, 6, 80),
      new THREE.MeshBasicMaterial({ color:0xF5A623, transparent:true, opacity:0.12 })
    ));

    /* Atmosphere glow */
    group.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.08, 24, 24),
      new THREE.MeshBasicMaterial({ color:0xF5A623, transparent:true, opacity:0.025, side:THREE.BackSide })
    ));

    /* Convert lat/lng → 3D */
    const toV3 = (lat, lng, r=1.015) => {
      const phi   = (90-lat)*Math.PI/180;
      const theta = (lng+180)*Math.PI/180;
      return new THREE.Vector3(-r*Math.sin(phi)*Math.cos(theta), r*Math.cos(phi), r*Math.sin(phi)*Math.sin(theta));
    };

    /* Client dots */
    const rings = [];
    CLIENT_LOCATIONS.forEach(loc => {
      const p = toV3(loc.lat, loc.lng);
      const dr = Math.min(0.018 + loc.clients*0.005, 0.033);
      // Amber dot
      const d = new THREE.Mesh(new THREE.SphereGeometry(dr, 8, 8), new THREE.MeshBasicMaterial({color:0xF5A623}));
      d.position.copy(p); group.add(d);
      // White core
      const wh = new THREE.Mesh(new THREE.SphereGeometry(dr*0.42, 6, 6), new THREE.MeshBasicMaterial({color:0xffffff}));
      wh.position.copy(p); group.add(wh);
      // Pulsing ring
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(dr*2.2, dr*0.3, 4, 20),
        new THREE.MeshBasicMaterial({color:0xF5A623, transparent:true, opacity:0.5})
      );
      ring.position.copy(p); ring.lookAt(0,0,0);
      ring.userData = { phase: Math.random()*Math.PI*2 };
      group.add(ring); rings.push(ring);
    });

    scene.add(new THREE.AmbientLight(0xffffff, 1));

    /* Drag */
    let dragging=false, prevX=0, prevY=0, autoSpin=true, velX=0;
    const onDown = e => { dragging=true; autoSpin=false; velX=0; prevX=e.clientX||e.touches?.[0]?.clientX||0; prevY=e.clientY||e.touches?.[0]?.clientY||0; };
    const onMove = e => {
      if (!dragging) return;
      const cx=e.clientX||e.touches?.[0]?.clientX||0, cy=e.clientY||e.touches?.[0]?.clientY||0;
      velX=(cx-prevX)*0.007;
      group.rotation.y+=velX;
      group.rotation.x=Math.max(-0.55,Math.min(0.55,group.rotation.x+(cy-prevY)*0.004));
      prevX=cx; prevY=cy;
    };
    const onUp = () => { dragging=false; setTimeout(()=>autoSpin=true,2000); };
    const el = renderer.domElement;
    el.addEventListener('mousedown',onDown); el.addEventListener('touchstart',onDown,{passive:true});
    window.addEventListener('mousemove',onMove); window.addEventListener('touchmove',onMove,{passive:true});
    window.addEventListener('mouseup',onUp); window.addEventListener('touchend',onUp);

    let raf, t=0;
    const animate = () => {
      raf=requestAnimationFrame(animate); t+=0.016;
      if(autoSpin) group.rotation.y+=0.0016;
      else if(!dragging){group.rotation.y+=velX;velX*=0.93;}
      rings.forEach(r=>{ const s=0.75+0.38*Math.sin(t*2.5+r.userData.phase); r.scale.setScalar(s); r.material.opacity=0.55*(1-s*0.3); });
      renderer.render(scene,camera);
    };
    animate();

    const ro = new ResizeObserver(()=>{ const nW=mount.clientWidth,nH=mount.clientHeight; camera.aspect=nW/nH; camera.updateProjectionMatrix(); renderer.setSize(nW,nH); });
    ro.observe(mount);

    return ()=>{
      cancelAnimationFrame(raf); ro.disconnect();
      el.removeEventListener('mousedown',onDown); el.removeEventListener('touchstart',onDown);
      window.removeEventListener('mousemove',onMove); window.removeEventListener('touchmove',onMove);
      window.removeEventListener('mouseup',onUp); window.removeEventListener('touchend',onUp);
      if(mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [size]);

  return <div ref={mountRef} className={size==='mini'?'globe-mini-canvas':'globe-full-canvas'}/>;
}


/* ── Globe Modal ─────────────────────────────────── */
function GlobeModal({ onClose, theme }) {
  return (
    <div className="gm-overlay" onClick={onClose}>
      <div className="gm-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="gm-hd">
          <div>
            <div className="gm-title">Live Client Map</div>
            <div className="gm-sub">{CLIENT_LOCATIONS.reduce((s,c)=>s+c.clients,0)} clients · {CLIENT_LOCATIONS.length} cities · drag to rotate</div>
          </div>
          <div className="gm-close" onClick={onClose}>✕</div>
        </div>
        <div className="gm-body">
          <Globe3D size="full"/>
          <div className="gm-list">
            <div className="gm-list-title">Active Clients</div>
            {CLIENT_LOCATIONS.map(loc=>(
              <div key={loc.city} className="gm-list-item">
                <div className="gm-dot"/>
                <div>
                  <div className="gm-city">{loc.city}</div>
                  <div className="gm-count">{loc.clients} client{loc.clients>1?'s':''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Chart Components ────────────────────────────── */
function StatCards() {
  return (
    <div className="an-stat-grid">
      {[
        {label:'Revenue this month',val:'₹2.58L',chg:'+8% vs Apr',color:'#F5A623'},
        {label:'Active leads',val:'48',chg:'+6 new',color:'#22C55E'},
        {label:'Tasks done',val:'94%',chg:'2 overdue',color:'#3B82F6'},
        {label:'Team present',val:'9/12',chg:'3 on leave',color:'#8B5CF6'},
      ].map(s=>(
        <div key={s.label} className="an-stat-card">
          <div className="an-stat-lbl">{s.label}</div>
          <div className="an-stat-val" style={{color:s.color}}>{s.val}</div>
          <div className="an-stat-chg">{s.chg}</div>
        </div>
      ))}
    </div>
  );
}

function RevenueLineChart() {
  const data = REVENUE.slice(0,5);
  const max  = Math.max(...data);
  const W=500,H=130,P=24;
  const pts  = data.map((v,i)=>({x:P+(i/(data.length-1))*(W-P*2),y:P+(1-v/max)*(H-P*2)}));
  const pathD= pts.map((p,i)=>`${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  const areaD= `${pathD} L${pts[pts.length-1].x},${H-P} L${pts[0].x},${H-P} Z`;
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Revenue Trend</span><span className="an-widget-tag">Jan–May 2026</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F5A623" stopOpacity="0.35"/><stop offset="100%" stopColor="#F5A623" stopOpacity="0"/></linearGradient></defs>
        {[0,1,2,3].map(i=><line key={i} x1={P} y1={P+i*(H-P*2)/3} x2={W-P} y2={P+i*(H-P*2)/3} stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.5"/>)}
        <path d={areaD} fill="url(#rg)"/>
        <path d={pathD} fill="none" stroke="#F5A623" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p,i)=><g key={i}><circle cx={p.x} cy={p.y} r="4" fill="#F5A623"/><circle cx={p.x} cy={p.y} r="2" fill="#fff"/><text x={p.x} y={H-4} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.4">{MONTHS[i]}</text></g>)}
      </svg>
    </div>
  );
}

function AreaChart() {
  const cumulative = REVENUE.slice(0,5).reduce((acc,v,i)=>[...acc,(acc[i-1]||0)+v],[]);
  const max = Math.max(...cumulative);
  const W=500,H=120,P=22;
  const pts = cumulative.map((v,i)=>({x:P+(i/(cumulative.length-1))*(W-P*2),y:P+(1-v/max)*(H-P*2)}));
  const pathD = pts.map((p,i)=>`${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L${pts[pts.length-1].x},${H-P} L${pts[0].x},${H-P} Z`;
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Cumulative Revenue</span><span className="an-widget-tag">Area chart</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22C55E" stopOpacity="0.4"/><stop offset="100%" stopColor="#22C55E" stopOpacity="0.02"/></linearGradient></defs>
        <path d={areaD} fill="url(#ag)"/>
        <path d={pathD} fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p,i)=><g key={i}><circle cx={p.x} cy={p.y} r="3.5" fill="#22C55E"/><text x={p.x} y={H-4} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.4">{MONTHS[i]}</text></g>)}
      </svg>
    </div>
  );
}

function VerticalBarChart() {
  const data = REVENUE.slice(0,5);
  const max  = Math.max(...data);
  const W=500,H=130,P=20,barW=55,gap=12;
  const total = data.length*(barW+gap);
  const startX = (W-total)/2;
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Monthly Revenue</span><span className="an-widget-tag">Bar chart</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        {data.map((v,i)=>{
          const bh = Math.max(4,(v/max)*(H-P*2));
          const x  = startX+i*(barW+gap);
          const y  = H-P-bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={bh} rx="3" fill="#F5A623" opacity="0.75"/>
              <rect x={x} y={y} width={barW} height={4} rx="3" fill="#F5A623"/>
              <text x={x+barW/2} y={H-4} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.45">{MONTHS[i]}</text>
              <text x={x+barW/2} y={y-4} textAnchor="middle" fontSize="8.5" fill="#F5A623" fontWeight="700">{fmtINR(v)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function PieChart() {
  const total = LEAD_SOURCES.reduce((s,d)=>s+d.value,0);
  let angle=-90;
  const R=58,CX=80,CY=72;
  const slices = LEAD_SOURCES.map(d=>{
    const sweep=(d.value/total)*360;
    const sA=angle*Math.PI/180, eA=(angle+sweep)*Math.PI/180;
    const laf=sweep>180?1:0;
    const path=`M${CX},${CY} L${CX+R*Math.cos(sA)},${CY+R*Math.sin(sA)} A${R},${R} 0 ${laf},1 ${CX+R*Math.cos(eA)},${CY+R*Math.sin(eA)} Z`;
    angle+=sweep; return {...d,path};
  });
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Lead Sources</span></div>
      <div className="an-pie-wrap">
        <svg viewBox="0 0 160 145" className="an-pie-svg">
          {slices.map(s=><path key={s.label} d={s.path} fill={s.color} opacity="0.85"/>)}
          <circle cx={CX} cy={CY} r="26" className="an-pie-hole"/>
          <text x={CX} y={CY+1} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="currentColor" opacity="0.5">Leads</text>
        </svg>
        <div className="an-pie-legend">{LEAD_SOURCES.map(s=><div key={s.label} className="an-pie-leg-item"><div className="an-pie-leg-dot" style={{background:s.color}}/><span className="an-pie-leg-lbl">{s.label}</span><span className="an-pie-leg-val">{s.value}%</span></div>)}</div>
      </div>
    </div>
  );
}

function PipelineChart() {
  const max = Math.max(...PIPELINE.map(p=>p.value));
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Lead Pipeline</span><span className="an-widget-tag">By status</span></div>
      <div className="an-bar-chart">{PIPELINE.map(p=>(
        <div key={p.label} className="an-bar-item">
          <div className="an-bar-meta"><span className="an-bar-lbl">{p.label}</span><span className="an-bar-val">{p.value}</span></div>
          <div className="an-bar-track"><div className="an-bar-fill" style={{width:`${(p.value/max)*100}%`,background:p.color}}/></div>
        </div>
      ))}</div>
    </div>
  );
}

function ScatterPlot() {
  const maxX=Math.max(...SCATTER_DATA.map(d=>d.x)),maxY=Math.max(...SCATTER_DATA.map(d=>d.y));
  const W=500,H=130,P=28;
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Leads vs Revenue</span><span className="an-widget-tag">Scatter plot</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        <line x1={P} y1={P} x2={P} y2={H-P} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.8"/>
        <line x1={P} y1={H-P} x2={W-P} y2={H-P} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.8"/>
        {[0,1,2,3].map(i=><line key={i} x1={P} y1={P+i*(H-P*2)/3} x2={W-P} y2={P+i*(H-P*2)/3} stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.5"/>)}
        {SCATTER_DATA.map((d,i)=>{
          const cx=P+(d.x/maxX)*(W-P*2);
          const cy=H-P-(d.y/maxY)*(H-P*2);
          return <g key={i}><circle cx={cx} cy={cy} r="7" fill="#8B5CF6" opacity="0.7"/><circle cx={cx} cy={cy} r="3" fill="#fff" opacity="0.9"/><text x={cx} y={cy-11} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5">{d.label}</text></g>;
        })}
        <text x={P-2} y={H-P+12} fontSize="8" fill="currentColor" opacity="0.35" textAnchor="middle">0</text>
        <text x={W/2} y={H-4} fontSize="8" fill="currentColor" opacity="0.35" textAnchor="middle">No. of Leads →</text>
      </svg>
    </div>
  );
}

function FunnelChart() {
  const max = FUNNEL[0].value;
  const W=460,H=160,P=16,barH=20,gap=6;
  const totalH=FUNNEL.length*(barH+gap)-gap;
  const startY=(H-totalH)/2;
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Conversion Funnel</span><span className="an-widget-tag">Lead stages</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="an-svg">
        {FUNNEL.map((f,i)=>{
          const pct=f.value/max;
          const bw=(W-P*2)*pct*0.85;
          const x=(W-bw)/2;
          const y=startY+i*(barH+gap);
          return (
            <g key={f.label}>
              <rect x={x} y={y} width={bw} height={barH} rx="4" fill={f.color} opacity="0.8"/>
              <text x={W/2} y={y+barH/2+1} textAnchor="middle" dominantBaseline="middle" fontSize="9.5" fill="#fff" fontWeight="600">{f.label}</text>
              <text x={W-P+4} y={y+barH/2+1} textAnchor="start" dominantBaseline="middle" fontSize="9" fill="currentColor" opacity="0.5">{f.value}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function KPIGauge() {
  const target=300000, current=258000;
  const pct=Math.min(current/target,1);
  const angle=-160+(pct*320);
  const R=70,CX=120,CY=110;
  const arcPath=(a1,a2,r)=>{
    const toRad=a=>a*Math.PI/180;
    const x1=CX+r*Math.cos(toRad(a1)), y1=CY+r*Math.sin(toRad(a1));
    const x2=CX+r*Math.cos(toRad(a2)), y2=CY+r*Math.sin(toRad(a2));
    const laf=a2-a1>180?1:0;
    return `M${x1},${y1} A${r},${r} 0 ${laf},1 ${x2},${y2}`;
  };
  const needleX=CX+R*0.85*Math.cos(angle*Math.PI/180);
  const needleY=CY+R*0.85*Math.sin(angle*Math.PI/180);
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Revenue Target</span><span className="an-widget-tag">KPI Gauge</span></div>
      <svg viewBox="0 0 240 130" className="an-svg">
        <path d={arcPath(-160,160,R)} fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="12" strokeLinecap="round"/>
        <path d={arcPath(-160,-160+pct*320,R)} fill="none" stroke="#F5A623" strokeWidth="12" strokeLinecap="round"/>
        <line x1={CX} y1={CY} x2={needleX} y2={needleY} stroke="#F5A623" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx={CX} cy={CY} r="5" fill="#F5A623"/>
        <text x={CX} y={CY+22} textAnchor="middle" fontSize="16" fontWeight="800" fill="#F5A623">{Math.round(pct*100)}%</text>
        <text x={CX} y={CY+36} textAnchor="middle" fontSize="8.5" fill="currentColor" opacity="0.4">of ₹3L target</text>
        <text x={40} y={CY+18} fontSize="8" fill="currentColor" opacity="0.3">0</text>
        <text x={192} y={CY+18} fontSize="8" fill="currentColor" opacity="0.3">₹3L</text>
      </svg>
    </div>
  );
}

function Heatmap() {
  const maxVal=10;
  const cellW=28,cellH=16,padL=28,padT=14;
  const hours=['6a','8a','10a','12p','2p','4p','6p','8p','10p','12a','2a','4a'];
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Activity Heatmap</span><span className="an-widget-tag">Day × Hour</span></div>
      <svg viewBox={`0 0 ${padL+12*cellW+20} ${padT+7*cellH+16}`} className="an-svg">
        {DAYS.map((d,di)=><text key={d} x={padL-4} y={padT+di*cellH+cellH*0.7} textAnchor="end" fontSize="7.5" fill="currentColor" opacity="0.4">{d}</text>)}
        {hours.map((h,hi)=><text key={h} x={padL+hi*cellW+cellW/2} y={padT-3} textAnchor="middle" fontSize="7" fill="currentColor" opacity="0.35">{h}</text>)}
        {HEATMAP_DATA.map((row,di)=>row.map((v,hi)=>{
          const intensity=v/maxVal;
          return <rect key={`${di}-${hi}`} x={padL+hi*cellW+1} y={padT+di*cellH+1} width={cellW-2} height={cellH-2} rx="2"
            fill={`rgba(245,166,35,${0.06+intensity*0.85})`}/>
        }))}
      </svg>
    </div>
  );
}

function TopClients() {
  const max=Math.max(...TOP_CLIENTS.map(c=>c.value));
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Top Clients</span></div>
      <div className="an-clients-list">{TOP_CLIENTS.map((c,i)=>(
        <div key={c.name} className="an-client-item">
          <div className="an-client-rank">#{i+1}</div>
          <div className="an-client-info"><div className="an-client-name">{c.name}</div><div className="an-client-city">{c.city}</div></div>
          <div className="an-client-bar-wrap"><div className="an-client-bar" style={{width:`${(c.value/max)*100}%`}}/></div>
          <div className="an-client-val">{fmtINR(c.value)}</div>
        </div>
      ))}</div>
    </div>
  );
}

function CustomMetric() {
  const [label,setLabel]=useState('Custom KPI');
  const [value,setValue]=useState('0');
  const [unit,setUnit]=useState('%');
  const [color,setColor]=useState('#F5A623');
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Custom Metric</span></div>
      <div className="an-custom-display" style={{color}}>{value}{unit}</div>
      <div className="an-custom-label">{label}</div>
      <div className="an-custom-form">
        <input className="an-custom-inp" placeholder="Label" value={label} onChange={e=>setLabel(e.target.value)}/>
        <input className="an-custom-inp" placeholder="Value" value={value} onChange={e=>setValue(e.target.value)}/>
        <input className="an-custom-inp" placeholder="Unit (%, ₹, K…)" value={unit} onChange={e=>setUnit(e.target.value)}/>
        <input className="an-custom-color" type="color" value={color} onChange={e=>setColor(e.target.value)} title="Pick color"/>
      </div>
    </div>
  );
}

const WIDGET_MAP = {
  stats:<StatCards key="stats"/>, revenue:<RevenueLineChart key="revenue"/>,
  area:<AreaChart key="area"/>,   vbar:<VerticalBarChart key="vbar"/>,
  sources:<PieChart key="sources"/>, pipeline:<PipelineChart key="pipeline"/>,
  scatter:<ScatterPlot key="scatter"/>, funnel:<FunnelChart key="funnel"/>,
  heatmap:<Heatmap key="heatmap"/>,
  clients:<TopClients key="clients"/>,
};

/* ── Main ─────────────────────────────────────────── */
export default function Analytics() {
  const { theme }  = useTheme();
  const [active, setActive]     = useState(DEFAULT_ACTIVE);
  const [showCustom,setShowCustom] = useState(false);
  const [showGlobe, setShowGlobe]  = useState(false);

  const toggleWidget = id => setActive(p=>p.includes(id)?p.filter(x=>x!==id):p.length<MAX_WIDGETS?[...p,id]:p);

  const TopActions = (
    <div className="an-top-acts">
      <div className="an-cust-btn" onClick={()=>setShowCustom(s=>!s)}>⚙ Customise</div>
    </div>
  );

  return (
    <div className="an-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="an-main">
        <Topbar title="Analytics" subtitle={`${active.length}/${MAX_WIDGETS} widgets active`} actions={TopActions}/>

        {showCustom && (
          <div className="an-cust-panel" data-theme={theme}>
            <div className="an-cust-title">Widgets <span className="an-cust-count">{active.length}/{MAX_WIDGETS}</span></div>
            <div className="an-cust-grid">
              {ALL_WIDGETS.map(w=>{
                const on=active.includes(w.id);
                const disabled=!on&&active.length>=MAX_WIDGETS;
                return (
                  <div key={w.id} className={`an-cust-item${on?' on':''}${disabled?' disabled':''}`} onClick={()=>!disabled&&toggleWidget(w.id)}>
                    <span className="an-cust-icon">{w.icon}</span>
                    <div><div className="an-cust-lbl">{w.label}</div><div className="an-cust-desc">{w.desc}</div></div>
                    <div className={`an-cust-tog${on?' on':''}`}><div className="an-cust-knob"/></div>
                  </div>
                );
              })}
            </div>
            {active.length>=MAX_WIDGETS&&<div className="an-cust-warn">Max {MAX_WIDGETS} widgets. Remove one to add another.</div>}
          </div>
        )}

        <div className="an-grid">
          {active.map(id=>WIDGET_MAP[id])}
        </div>

        {/* Globe mini button */}
        <div className="globe-mini-btn" onClick={()=>setShowGlobe(true)} data-theme={theme}>
          <div className="globe-mini-preview"><Globe3D size="mini"/></div>
          <div className="globe-mini-info">
            <div className="globe-mini-label">Live Map</div>
            <div className="globe-mini-sub">{CLIENT_LOCATIONS.reduce((s,c)=>s+c.clients,0)} clients</div>
          </div>
        </div>
      </div>

      {showGlobe && <GlobeModal onClose={()=>setShowGlobe(false)} theme={theme}/>}
    </div>
  );
}
